import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { QrCode, ShieldCheck, Loader2, ArrowLeft } from "lucide-react";

export default function MFAVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyMFA } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const tempToken = location.state?.tempToken;
  const email = location.state?.email;

  if (!tempToken) {
    navigate("/login");
    return null;
  }

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      await verifyMFA(tempToken, code);
      toast.success("Authentication successful!");
      navigate("/dashboard");
    } catch (error) {
      console.error("MFA verification error:", error);
      toast.error(error.response?.data?.detail || "Invalid code. Please try again.");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="mfa-verify-page">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">STPmembership</h1>
              <p className="text-sm text-gray-500">Two-Factor Authentication</p>
            </div>
          </div>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1 pb-4 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-2">
              <ShieldCheck className="w-7 h-7 text-blue-600" />
            </div>
            <CardTitle className="text-2xl font-semibold">Verify your identity</CardTitle>
            <CardDescription>
              Enter the 6-digit code from your authenticator app
              {email && (
                <span className="block mt-1 text-gray-700 font-medium">{email}</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                value={code}
                onChange={setCode}
                maxLength={6}
                disabled={loading}
                data-testid="mfa-code-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button 
              onClick={handleVerify}
              className="w-full h-11 btn-press" 
              disabled={loading || code.length !== 6}
              data-testid="verify-mfa-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </Button>

            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate("/login")}
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Open your authenticator app to get the code
        </p>
      </div>
    </div>
  );
}
